import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Intercom } from '@supy-io/ngx-intercom';
import { DateTime } from 'luxon';
import { EnvironmentService } from 'src/app/common/services/environment/environment.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { getAbbrCurrentTimezoneString } from 'src/app/common/utilities/time-helpers';
import { PdSession } from '../../../dtos/pd.dto';
import { AlertService, AlertType } from '../../../services/alert/alert.service';
import { LMSCourseService } from '../../../services/lms-courses/lms-course.service';
import { PdService } from '../../../services/pd/pd.service';

@Component({
  selector: 'app-pd-list-item',
  templateUrl: './pd-list-item.component.html',
  styleUrl: './pd-list-item.component.scss',
  standalone: false,
})
export class PdListItemComponent implements OnInit {
  @Input() session: PdSession;

  @Input() context: 'normal' | 'copy' = 'normal';

  @Input() userId: number;

  @Output() readonly sessionCopied = new EventEmitter<PdSession>();

  @Output() readonly sessionDeletedNotification = new EventEmitter<boolean>();

  public API_URL = EnvironmentService.apiUrl();

  isRegistered: boolean;

  isWaitlist: boolean;

  isFacilitator: boolean;

  isComplete: number;

  sessionOverdue: boolean;

  courseLink: string;

  backgroundImage: string;

  isInRegistrationWindow: boolean;

  abbrTimezone: string;

  reachedMaxRegistrations: boolean;

  user: User;

  constructor(
    private store: Store,
    private pdService: PdService,
    private alertService: AlertService,
    private intercom: Intercom,
    private router: Router,
    private lmsCourseService: LMSCourseService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit() {
    if (this.session && this.userId) {
      this.abbrTimezone = getAbbrCurrentTimezoneString();
      this.checkifSessionPast();
      this.isComplete = this.session.is_completed;
      if (this.session.facilitators && this.session.facilitators.length > 0) {
        this.isFacilitator = this.session.facilitators.some(
          (facilitator) => facilitator.facilitator_user_id === this.userId
        );
      }
      if (this.session.attendees && this.session.attendees.length > 0) {
        this.isRegistered = this.session.attendees.some(
          (attendee) => attendee.user_id === this.userId
        );
      }
      if (this.session.waitlist && this.session.waitlist.length > 0) {
        this.isWaitlist = this.session.waitlist.some(
          (waitlist) => waitlist.user_id === this.userId
        );
        if (this.isWaitlist) {
          this.isRegistered = true;
        }
      }
      if (this.session.image) {
        this.backgroundImage = `url(${this.API_URL}${this.session.image})`;
      } else {
        this.backgroundImage = 'url(/assets/pd_list_item_default.png)';
      }

      this.isInRegistrationWindow =
        this.session.registration_start_datetime <=
          DateTime.now().toSeconds() &&
        this.session.registration_end_datetime >= DateTime.now().toSeconds();
      this.reachedMaxRegistrations =
        this.session.max_registrations > 0 &&
        this.session.attendees.length >= this.session.max_registrations;
    }
  }

  register(sessionId: number) {
    this.pdService.registerSession(sessionId).subscribe({
      error: (error) => {
        this.alertService.showAlert(
          error.error.message,
          undefined,
          AlertType.DANGER
        );
      },
      next: (response) => {
        const metadata = {
          pd_session_id: response.pd_session_id,
          user_id: response.user_id,
          district_id: response.user.district_id,
        };
        this.intercom.trackEvent('PD Self Registration', metadata);

        if (this.session.lms_course_id) {
          this.intercom.trackEvent('Microlearning Self Registration', metadata);
          this.lmsCourseService
            .createCourseRegistration(this.session.lms_course_id, this.user.id)
            .subscribe((resp) => {
              if (resp) {
                if (resp.error) {
                  this.alertService.showAlert(
                    resp.error,
                    undefined,
                    AlertType.DANGER
                  );
                } else {
                  this.isRegistered = true;
                  if (response.is_waitlist) {
                    this.isWaitlist = true;
                  }
                }
              }
            });
        } else {
          this.isRegistered = true;
          if (response.is_waitlist) {
            this.isWaitlist = true;
          }
        }
      },
    });
  }

  unregister(sessionId: number, fromWaitlist = false) {
    this.pdService
      .unregisterSession(sessionId, this.userId, fromWaitlist)
      .subscribe(() => {
        this.isRegistered = false;
        if (!this.isWaitlist) {
          this.session.attendees.length -= 1;
        }
        this.reachedMaxRegistrations =
          this.session.max_registrations > 0 &&
          this.session.attendees.length >= this.session.max_registrations;
      });
  }

  checkifSessionPast() {
    // allows users to register for full day of event
    const todaysDate = new Date();
    const sessionDate = new Date(this.session.end_datetime * 1000);
    sessionDate.setHours(25, 0, 0);
    this.sessionOverdue = todaysDate > sessionDate;
  }

  handleSelect(session: PdSession) {
    this.sessionCopied.next(session);
  }

  navigateToCreatePage(session: PdSession) {
    this.router.navigate(['/training/create-session/microlearning'], {
      state: { sessionToContinue: session },
    });
  }

  deleteEvent(sessionId: number) {
    this.pdService.deleteSession(sessionId).subscribe((res) => {
      if (res) {
        this.sessionDeletedNotification.emit(true);
      }
    });
  }
}
